/**
 * Scope precedence: wider scopes satisfy narrower requirements.
 *   org > department > team > self
 *
 * i.e. a user with `leave/approve/org` also satisfies a `leave/approve/team` check.
 */
const SCOPE_RANK = {
    self: 0,
    team: 1,
    department: 2,
    org: 3,
};
/**
 * Evaluate whether an actor may perform (module, action) at the requested scope
 * against the resource described by `resource`.
 *
 * `resource` is only needed when the required scope is 'self', 'team', or 'department'.
 * For 'org' scope it's ignored (RLS already scoped rows to this tenant).
 */
export function hasPermission(actor, module, action, requiredScope, resource) {
    const required = SCOPE_RANK[requiredScope];
    const matching = actor.permissions.filter((perm) => perm.module === module && perm.action === action);
    if (matching.length === 0)
        return false;
    return matching.some((perm) => {
        const granted = SCOPE_RANK[perm.scope];
        if (granted < required)
            return false;
        return scopeCovers(actor, perm.scope, requiredScope, resource);
    });
}
/**
 * Given the actor's granted scope and the required scope, decide whether the
 * concrete resource falls inside the actor's reach. `org` always wins because
 * RLS already ensures cross-tenant isolation.
 */
function scopeCovers(actor, grantedScope, requiredScope, resource) {
    if (grantedScope === 'org')
        return true;
    if (grantedScope === 'department') {
        if (requiredScope === 'org')
            return false;
        if (!resource?.ownerDepartmentId)
            return true; // org-level list â€” filter downstream
        return resource.ownerDepartmentId === actor.departmentId;
    }
    if (grantedScope === 'team') {
        if (requiredScope === 'org' || requiredScope === 'department')
            return false;
        return isInTeamOf(actor, resource);
    }
    // self
    if (requiredScope !== 'self')
        return false;
    return isSelf(actor, resource);
}
function isSelf(actor, resource) {
    if (!resource)
        return true; // the actor is asking about their own bucket
    if (resource.ownerUserId && resource.ownerUserId === actor.userId)
        return true;
    if (resource.ownerEmployeeId && resource.ownerEmployeeId === actor.employeeId)
        return true;
    return false;
}
function isInTeamOf(actor, resource) {
    if (!resource)
        return true; // team-scoped listing â€” repository must filter by managerChain
    if (isSelf(actor, resource))
        return true;
    const ownerChain = resource.ownerManagerChain ?? [];
    return ownerChain.includes(actor.employeeId ?? '__none__');
}
/**
 * Convenience: does the actor have *any* permission on the given module?
 * Used by the frontend to decide whether to show a nav item at all.
 */
export function canAccessModule(actor, module) {
    return actor.permissions.some((perm) => perm.module === module);
}
//# sourceMappingURL=has-permission.js.map